import { Tooltip, Typography } from '@material-ui/core';
import { contextActions } from 'app/core/redux/context-redux';
import Button from 'app/shared/components/button/Button';
import Icon from 'app/shared/components/icon/Icon';
import IconLib, {
  IWarrantyLogoSvg,
} from 'app/shared/components/icon/icon-lib';
import styledMixins from 'app/styles/styledMixins';
import useSafeAreaInsets from 'app/tools/hooks/use-safe-area-insets';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components/macro';
import SharedRoutePath from '../SharedRoutePath';

const Container = styled.div<{ $noLogo: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: ${p =>
    p.$noLogo ? 'flex-start' : 'space-between'};
  align-items: center;
  padding: 2px;
  padding-top: 2px;
  ${styledMixins.softShadowTiny};

  > * {
    margin-top: 4px;
  }
`;

const Title = styled(Typography)<{ component: 'h1' }>`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const IconBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1rem; // reset size inherited from MuiButton-base
  width: 28px;
  height: 28px;
  overflow: visible;
`;

const SpacedButton = styled(Button)`
  margin-left: 6px;
  margin-right: 6px;
`;

const BackIconStyled = styled(Icon)`
  position: relative;
  left: -3px;
  margin-right: 0px;
`;

export const BackButton: React.VFC<{ absolutePos?: boolean }> = ({
  absolutePos: topLeft,
}) => {
  const { top } = useSafeAreaInsets();
  return (
    <SpacedButton
      iconButton
      navBackAction
      aria-label="back"
      css={topLeft && `top: ${top}; left: 0; position: absolute;`}
    >
      <IconBox>
        <BackIconStyled
          icon={IconLib.CHEVRON_LEFT}
          size={32}
          color="primary"
        />
      </IconBox>
    </SpacedButton>
  );
};

const IWarrantyIcon = styled(Icon)`
  position: relative;
  left: -2px;
  top: -4px;
`;

type Props = {
  title?: string;
  backButton?: boolean;
  noLogo?: boolean;
};

export default function HeaderBar({
  title = '',
  backButton,
  noLogo,
}: Props): JSX.Element {
  const dispatch = useDispatch();
  const [titleEl, setTitleEl] = useState<HTMLElement | null>(null);
  const [someTextHidden, setSomeTextHidden] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // NOTE: this is in a useEffect to ensure it runs after the last update,
    // to catch any changes to titleEl property values. If someTextHidden
    // has not changed, then no second component update will be triggered.
    // someTextHidden does not effect titleEl, so no infinite loops created.
    setSomeTextHidden(
      !!titleEl && titleEl.offsetWidth < titleEl.scrollWidth
    );
  });

  const { top } = useSafeAreaInsets();

  return (
    <Container
      $noLogo={!!noLogo}
      css={`
        padding-top: ${Math.max(2, top * 0.8)}px;
      `}
    >
      {backButton ? (
        <BackButton />
      ) : (
        <SpacedButton
          iconButton
          aria-label="menu"
          onClick={() => dispatch(contextActions.menuOpen.set(true))}
        >
          <IconBox>
            <Icon icon={IconLib.MENU} size={32} color="primary" />
          </IconBox>
        </SpacedButton>
      )}
      <Tooltip
        {...{ title }}
        arrow
        disableTouchListener={!someTextHidden}
        disableHoverListener={!someTextHidden}
      >
        <Title variant="h5" component="h1" ref={setTitleEl}>
          {title}
        </Title>
      </Tooltip>
      {!noLogo && (
        <SpacedButton iconButton to={SharedRoutePath.HOME}>
          <IconBox>
            <IWarrantyIcon imgNode={<IWarrantyLogoSvg />} size={38} />
          </IconBox>
        </SpacedButton>
      )}
    </Container>
  );
}
